import {
    ConditionalAccessApplications,
    ConditionalAccessConditionSet,
    ConditionalAccessGrantControls,
    ConditionalAccessGuestOrExternalUserTypes,
    ConditionalAccessPolicy,
    ConditionalAccessSessionControls,
    ConditionalAccessUsers,
    NullableOption,
} from '@microsoft/microsoft-graph-types-beta';
import { replaceRetiredGrant, usesRetiredGrant } from './cap-remediate';
import { getCAPBaselineDisplayName } from './cap-schema';
import { GenericCapSchema_Data } from 'projects/angular-clarity/src/app/modules/sway/models/baseline-schemas/generic-cap-schema-model';
import { ArrayTypeSchema } from 'projects/angular-clarity/src/app/modules/sway/models/schema';
import {
    createConditionalAccessPolicy,
    deleteConditionalAccessPolicy,
    updateConditionalAccessPolicy,
} from 'projects/angular-clarity/src/app/stores/client/graph/conditional-access/actions';

const getGrantControls = (schema: ArrayTypeSchema<GenericCapSchema_Data>): ConditionalAccessGrantControls => {
    const builtInControls = schema.contains.properties.grantControls.properties?.builtInControls?.items?.enum || [];
    const operator = schema.contains.properties.grantControls.properties?.operator.const;
    const authenticationStrengthId =
        schema.contains.properties.grantControls.properties?.authenticationStrength?.properties?.id?.const;

    // null is very important to reset any old data
    const authenticationStrength = authenticationStrengthId
        ? {
              id: schema.contains.properties.grantControls.properties?.authenticationStrength?.properties?.id?.const,
          }
        : null;

    if (!authenticationStrength && builtInControls.length === 0) {
        return null;
    }

    return replaceRetiredGrant({
        operator,
        builtInControls,
        authenticationStrength,
    });
};

const getSessionControls = (schema: ArrayTypeSchema<GenericCapSchema_Data>): ConditionalAccessSessionControls => {
    const sessionControls = schema.contains.properties.sessionControls;

    const disableResilienceDefaults = sessionControls?.properties?.disableResilienceDefaults?.const || null;

    const continuousAccessEvaluationMode =
        sessionControls?.properties?.continuousAccessEvaluation?.properties?.mode?.const;

    const continuousAccessEvaluation = continuousAccessEvaluationMode
        ? { mode: sessionControls?.properties?.continuousAccessEvaluation?.properties?.mode?.const }
        : null;

    const signInFrequencyEnabled = sessionControls?.properties?.signInFrequency?.properties?.isEnabled?.const;

    const signInFrequency = signInFrequencyEnabled
        ? {
              isEnabled: signInFrequencyEnabled,
              frequencyInterval: sessionControls?.properties?.signInFrequency?.properties?.frequencyInterval?.const,
              value: sessionControls?.properties?.signInFrequency?.properties?.value?.const,
              type: sessionControls?.properties?.signInFrequency?.properties?.type?.const,
              authenticationType: sessionControls?.properties?.signInFrequency?.properties?.authenticationType?.const,
          }
        : null;

    const cloudAppSecurityEnable = sessionControls?.properties?.cloudAppSecurity?.properties?.isEnabled?.const;

    const cloudAppSecurity = cloudAppSecurityEnable
        ? {
              isEnabled: cloudAppSecurityEnable,
              cloudAppSecurityType:
                  sessionControls?.properties?.cloudAppSecurity?.properties?.cloudAppSecurityType?.const,
          }
        : null;

    const persistentBrowserEnabled = sessionControls?.properties?.persistentBrowser?.properties?.isEnabled?.const;

    const persistentBrowser = persistentBrowserEnabled
        ? {
              isEnabled: persistentBrowserEnabled,
              mode: sessionControls?.properties?.persistentBrowser?.properties?.mode?.const,
          }
        : null;

    return {
        disableResilienceDefaults,
        continuousAccessEvaluation,
        signInFrequency,
        cloudAppSecurity,
        persistentBrowser,
    };
};

const getConditions = (
    schema: ArrayTypeSchema<GenericCapSchema_Data>,
): ConditionalAccessConditionSet & { insiderRiskLevels?: string; authenticationFlows: any } => {
    const includeRoles =
        schema?.contains?.properties?.conditions?.properties?.users?.properties?.includeRoles?.items?.enum || [];

    const excludeRoles =
        schema?.contains?.properties?.conditions?.properties?.users?.properties?.excludeRoles?.items?.enum || [];
    const includeUsers =
        schema?.contains?.properties?.conditions?.properties?.users?.properties?.includeUsers?.items?.enum || [];
    const excludeUsers =
        schema?.contains?.properties?.conditions?.properties?.users?.properties?.excludeUsers?.items?.enum || [];
    const includeGroups =
        schema?.contains?.properties?.conditions?.properties?.users?.properties?.includeGroups?.items?.enum || [];
    const excludeGroups =
        schema?.contains?.properties?.conditions?.properties?.users?.properties?.excludeGroups?.items?.enum || [];

    const users: ConditionalAccessUsers = {
        includeUsers,
        excludeUsers,
        includeGroups,
        excludeGroups,
        includeRoles,
        excludeRoles,
    };

    const userRiskLevels = schema?.contains?.properties?.conditions?.properties?.userRiskLevels?.items?.enum || [];

    const signInRiskLevels = schema?.contains?.properties?.conditions?.properties?.signInRiskLevels?.items?.enum || [];

    const insiderRiskLevels = getStringFromPattern(
        schema?.contains?.properties?.conditions?.properties?.insiderRiskLevels?.pattern,
    );
    const clientAppTypes = schema?.contains?.properties?.conditions?.properties?.clientAppTypes?.items?.enum || ['all'];
    // if it is not configured it is default to all;

    const transferMethods = getStringFromPattern(
        schema?.contains?.properties?.conditions?.properties?.authenticationFlows?.properties?.transferMethods?.pattern,
    );

    const authenticationFlows = transferMethods
        ? {
              transferMethods,
          }
        : null;

    const includePlatforms =
        schema?.contains?.properties?.conditions?.properties?.platforms?.properties?.includePlatforms?.items?.enum ||
        [];
    const excludePlatforms =
        schema?.contains?.properties?.conditions?.properties?.platforms?.properties?.excludePlatforms?.items?.enum ||
        [];
    const platforms =
        includePlatforms?.length > 0
            ? {
                  includePlatforms,
                  excludePlatforms,
              }
            : null;

    const includeLocations =
        schema?.contains?.properties?.conditions?.properties?.locations?.properties?.includeLocations?.items?.enum ||
        [];
    const excludeLocations =
        schema?.contains?.properties?.conditions?.properties?.locations?.properties?.excludeLocations?.items?.enum ||
        [];
    const locations =
        includeLocations?.length > 0
            ? {
                  includeLocations,
                  excludeLocations,
              }
            : null;

    const applications = {
        // if it is not configured it is default to None
        includeApplications: schema?.contains?.properties?.conditions?.properties?.applications?.properties
            ?.includeApplications?.items?.enum || ['None'],
    };

    return {
        users,
        userRiskLevels,
        signInRiskLevels,
        insiderRiskLevels,
        platforms,
        locations,
        clientAppTypes,
        applications,
        authenticationFlows,
    };
};

const CAP_Policy = (items: {
    displayName;
    sessionControls: ConditionalAccessSessionControls;
    grantControls: NullableOption<ConditionalAccessGrantControls>;
    state: 'enabled' | 'disabled';
    conditions: NullableOption<ConditionalAccessConditionSet>;
}): ConditionalAccessPolicy => {
    const { displayName, sessionControls, grantControls, state, conditions } = items;

    return {
        displayName: displayName,
        state: state,
        conditions,

        sessionControls: sessionControls,
        grantControls: grantControls,
    };
};

function getStringFromPattern(pattern: string): string {
    return pattern?.split(')')?.join('')?.replace('(?=.*', '').split('(?=.*')?.join(',') || null;
}

export function remediateGenericCapPolicy(_tenant: string, schema: any, policy: ConditionalAccessPolicy) {
    const displayName = getCAPBaselineDisplayName(schema);

    const isNoPolicySchema = !!schema?.not?.contains;
    const state = schema?.contains?.properties?.state?.const;

    const includeGuestsOrExternalUsersPattern: string =
        schema?.contains?.properties?.conditions?.properties?.users?.properties?.includeGuestsOrExternalUsers
            ?.properties?.guestOrExternalUserTypes?.pattern || null;

    const includeGuestsOrExternalUsers: string = getStringFromPattern(includeGuestsOrExternalUsersPattern);

    const excludeGuestsOrExternalUsersPattern: string =
        schema?.contains?.properties?.conditions?.properties?.users?.properties?.excludeGuestsOrExternalUsers
            ?.properties?.guestOrExternalUserTypes?.pattern || null;

    const excludeGuestsOrExternalUsers: string = getStringFromPattern(excludeGuestsOrExternalUsersPattern);

    if (!policy) {
        // create MFA Policy if it is not exists
        const new_policy: ConditionalAccessPolicy = JSON.parse(
            JSON.stringify(
                CAP_Policy({
                    displayName: displayName,
                    sessionControls: getSessionControls(schema),
                    grantControls: getGrantControls(schema),
                    state,
                    conditions: getConditions(schema),
                }),
            ),
        );

        // TODO, karim fix this
        if (includeGuestsOrExternalUsers) {
            new_policy.conditions.users['includeGuestsOrExternalUsers'] = {};
            new_policy.conditions.users['includeGuestsOrExternalUsers'].guestOrExternalUserTypes =
                includeGuestsOrExternalUsers as ConditionalAccessGuestOrExternalUserTypes;
        } else {
            new_policy.conditions.users['includeGuestsOrExternalUsers'] = null;
        }

        if (excludeGuestsOrExternalUsers) {
            new_policy.conditions.users['excludeGuestsOrExternalUsers'] = {};
            new_policy.conditions.users['excludeGuestsOrExternalUsers'].guestOrExternalUserTypes =
                excludeGuestsOrExternalUsers as ConditionalAccessGuestOrExternalUserTypes;
        } else {
            new_policy.conditions.users['excludeGuestsOrExternalUsers'] = null;
        }

        return createConditionalAccessPolicy({ _tenant, policy: new_policy });
    } else if (policy) {
        if (isNoPolicySchema || usesRetiredGrant(policy)) {
            return deleteConditionalAccessPolicy({ _tenant, id: policy.id });
        } else {
            const updated_policy = JSON.parse(
                JSON.stringify(
                    CAP_Policy({
                        displayName: displayName,
                        sessionControls: getSessionControls(schema),
                        grantControls: getGrantControls(schema),
                        state,
                        conditions: getConditions(schema),
                    }),
                ),
            );

            if (includeGuestsOrExternalUsers) {
                updated_policy.conditions.users['includeGuestsOrExternalUsers'] = {};
                updated_policy.conditions.users['includeGuestsOrExternalUsers'].guestOrExternalUserTypes =
                    includeGuestsOrExternalUsers;
            } else {
                updated_policy.conditions.users['includeGuestsOrExternalUsers'] = null;
            }

            if (excludeGuestsOrExternalUsers) {
                updated_policy.conditions.users['excludeGuestsOrExternalUsers'] = {};
                updated_policy.conditions.users['excludeGuestsOrExternalUsers'].guestOrExternalUserTypes =
                    excludeGuestsOrExternalUsers;
            } else {
                updated_policy.conditions.users['excludeGuestsOrExternalUsers'] = null;
            }

            return updateConditionalAccessPolicy({ _tenant, policy: { id: policy.id, ...updated_policy } });
        }
    }
}
